import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Catalogue genre/category names are codes (the backend's wearer_group/subcategory
// names, or the lowercase URL segment built from them). The text shown to the user
// comes from catalogueMenu.genders/categories, keyed by the code normalized to
// lowercase words joined by '_' — so "POLO - T-SHIRT", "polo - t-shirt" and
// "POLO/T-SHIRT" all resolve to the same key. Unknown codes fall back to the code.
// The database labels from the menu are registered over that dictionary at load time
// (helpers/registerCatalogueLabels).
export const normalizeMenuKey = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const useCatalogueLabels = () => {
  // `t` gets a new identity on every language change, so the callbacks below
  // (and any effect that depends on them) re-run when the user switches language.
  const { t } = useTranslation();

  const genreLabel = useCallback(
    (genre: string): string =>
      String(
        t(`catalogueMenu.genders.${normalizeMenuKey(genre)}` as any, {
          defaultValue: genre,
        })
      ),
    [t]
  );

  const categoryLabel = useCallback(
    (category: string): string =>
      String(
        t(`catalogueMenu.categories.${normalizeMenuKey(category)}` as any, {
          defaultValue: category,
        })
      ),
    [t]
  );

  return { genreLabel, categoryLabel };
};
